import type { AriaAttributes, KeyboardEvent, Ref } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import _ from 'lodash';
import type { ClassNameProp } from '../common-types';
import { Direction } from '../common-types';
import type { CommonInputProps } from './CommonInputProps';
import type { ValidIconName } from '../basic';
import { Icon } from '../basic';
import { noPropagationClickHandler } from '../../lib';
import { ValidationErrorMessage } from './internal';

/**
 * Props for the {@link Toggle} component.
 *
 */
export interface CheckboxProps
  extends ClassNameProp,
    CommonInputProps<boolean>,
    Pick<AriaAttributes, 'aria-label' | 'aria-describedby'> {
  /**
   * An optional custom color to use when the input is active.
   *
   * This is not supported by IE - if you need it outside the patient portal, use the classes as documented in {@link Toggle} and {@link Checkbox}.
   */
  color?: string;

  /**
   * An optional label component that may render more complex sub-components (like hyperlinks inside the label).
   *
   * This has to be seen as possible extension for the regular label (which is always used as aria-label text).
   */
  labelComponent?: JSX.Element;
  /**
   * Custom icon for checked value.
   */
  checkedIcon?: ValidIconName;
  /**
   * Custom icon for unchecked value.
   */
  uncheckedIcon?: ValidIconName;
  /**
   * Placement of the checkbox label.
   */
  labelPlacement?: Direction;
  /**
   * Whether checkbox should be in indeterminate state
   */
  indeterminate?: boolean;
  /**
   * Defines whether the checkbox should become active when clicked on intermediate state
   */
  setCheckedOnIndeterminateClick?: boolean;
}

/**
 * A Material Design Toggle component.
 *
 */
const BooleanInput = forwardRef(function BooleanInput(
  {
    debounceWait = 1000,
    indeterminate = false,
    setCheckedOnIndeterminateClick = true,
    defaultValue,
    value,
    onChange,
    onFocus,
    onBlur,
    readOnly,
    className: classNameProp,
    label: labelProp,
    labelPlacement = Direction.RIGHT,
    labelComponent,
    required,
    disabled,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    error,
    color,
    mode,
    checkedIcon: checkedIconProp,
    uncheckedIcon: uncheckedIconProp,
    ...props
  }: Readonly<CheckboxProps> & {
    mode: 'toggle' | 'checkbox';
    indeterminate?: boolean;
  },
  ref: Ref<HTMLInputElement>
): JSX.Element {
  const controlled = value !== undefined;
  const [state, setState] = useState(value);
  useEffect(() => {
    setState(value);
  }, [value]);

  // decorate change callback event with debounce if necessary
  let onChangeHandler = onChange;
  if (!controlled && onChange && debounceWait > 0) {
    onChangeHandler = _.debounce(onChange, debounceWait);
  }

  const changeHandler = useCallback(() => {
    if (disabled || readOnly) {
      return;
    }
    const newState =
      !indeterminate || (indeterminate && !setCheckedOnIndeterminateClick)
        ? !state
        : !!state;
    setState(newState);
    onChangeHandler?.(newState);
  }, [
    disabled,
    indeterminate,
    onChangeHandler,
    readOnly,
    setCheckedOnIndeterminateClick,
    state
  ]);

  let wrapperClassName = `ehs-${mode}-wrapper`;
  if (classNameProp) wrapperClassName += ` ${classNameProp}`;

  const label = useMemo(() => (required ? `${labelProp} *` : labelProp), [
    labelProp,
    required
  ]);

  let className = `ehs-${mode} label-${labelPlacement}`;
  if (error) className += ' invalid';
  else className += ' valid';
  if (disabled) className += ' disabled';

  // support custom colors
  const boxStyle = state && color ? { color: color } : undefined;
  const handleStyle = state && color ? { backgroundColor: color } : undefined;
  const trackStyle =
    state && color ? { ...handleStyle, opacity: 0.3 } : undefined;

  const icon = useMemo(() => {
    const uncheckedIcon =
      uncheckedIconProp || 'materialicon-check_box_outline_blank';
    const checkedIcon = checkedIconProp || 'materialicon-check_box';
    const indeterminateIcon = 'materialicon-indeterminate_check_box';

    if (!state) {
      return uncheckedIcon;
    } else if (indeterminate) {
      return indeterminateIcon;
    } else {
      return checkedIcon;
    }
  }, [checkedIconProp, indeterminate, state, uncheckedIconProp]);

  const onClickHandler = useMemo(
    () => noPropagationClickHandler(changeHandler),
    [changeHandler]
  );
  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.stopPropagation();
        changeHandler();
      }
    },
    [changeHandler]
  );
  const ariaDescribedByIds = [ariaDescribedBy];
  let errorId = '';
  if (error) {
    errorId = _.uniqueId('ehs-toggle-input-error-');
    ariaDescribedByIds.push(errorId);
  }

  return (
    <div {...props} className={wrapperClassName}>
      <div
        className={className}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
      >
        <span>
          <input
            ref={ref}
            type="checkbox"
            disabled={disabled}
            readOnly={readOnly}
            defaultChecked={defaultValue}
            checked={value === undefined || indeterminate ? undefined : state}
            aria-required={required}
            aria-readonly={readOnly}
            aria-invalid={!!error}
            aria-label={ariaLabel ?? label}
            aria-checked={state && indeterminate ? 'mixed' : state}
            aria-describedby={ariaDescribedByIds.join(' ').trim() || undefined}
          />

          {mode === 'toggle' && (
            <>
              <span
                style={trackStyle}
                className={`track ${state ? 'active' : ''}`}
              />
              <span
                style={handleStyle}
                className={`handle ${state ? 'active' : ''}`}
              />
            </>
          )}

          {mode === 'checkbox' && (
            <Icon
              style={boxStyle}
              aria-label={undefined}
              className={`box ${state ? 'active' : ''}`.trim()}
              iconName={icon}
            />
          )}
        </span>

        <label aria-hidden={true}>{labelComponent ?? label}</label>
      </div>
      {error && <ValidationErrorMessage id={errorId} error={error} />}
    </div>
  );
});

/**
 * A Material Design Toggle component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .toggle .handle.active { background-color: ... }
 * .toggle .track.active { opacity: 0.3; background-color: ...}
 * ```
 *
 */
export const Toggle = forwardRef(function Toggle(
  props: Readonly<
    Omit<
      CheckboxProps,
      | 'checkedIcon'
      | 'uncheckedIcon'
      | 'indeterminate'
      | 'setCheckedOnIndeterminateClick'
    >
  >,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  return <BooleanInput {...props} mode="toggle" ref={ref} />;
});

/**
 * A Material Design Checkbox component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .checkbox .box.active { color: ... }
 * ```
 *
 */
export const Checkbox = forwardRef(function Checkbox(
  props: Readonly<CheckboxProps>,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  return <BooleanInput {...props} mode="checkbox" ref={ref} />;
});
